import React, { FC, useState, useRef, useId, useEffect } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import {
  Button,
  Eyebrow,
  EyebrowEnum,
  Heading,
  headingType,
  Icon,
  size,
  Image,
  LogoContainer,
  tags,
  LogoContainerBackgroundColor,
  useCheckBreakpoint,
  useWindowSize,
  Types,
  getIconClassFromToken,
} from '@marriott/mi-ui-library';
import { ProductDetailsCardProps } from './ProductDetailsCard.types';
import {
  StyledProductDetailsCard,
  StyledEyebrow,
  StyledTitleRow,
  StyledTags,
  TagsWrapper,
  StyledBadge,
} from './ProductDetailsCard.styles';
import { useImageLoadTracker } from '../../hooks';

const ProductDetailsCardComp: FC<ProductDetailsCardProps> = ({
  className,
  componentId,
  styleclass,
  cardVariation,
  cardUrl,
  openInaNewTab,
  icon,
  eyebrow,
  badgeIcon,
  badgeText,
  title,
  tagListItems,
  subTitle,
  description,
  brandLogoIcon,
  fileReferenceImage,
  imageAltText,
  dynamicMedia,
  dynamicMediaTall,
  dynamicMediaWide,
  trackingProperties,
  breakPointKey = 'viewportXl', // default value for desktop is greater than 1200
}) => {
  const pdcRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  // Generate unique ids to prevent screenreaders from reading only the first card
  const id = useId();
  const renderedTagListItems = badgeText ? tagListItems?.slice(0, 1) : tagListItems;
  const renderTags = (tagListItems: { tagLabel: string }[] | undefined) =>
    tagListItems &&
    tagListItems.length > 0 && (
      <StyledTags id={`${id}tags`} role="list">
        {renderedTagListItems?.map(tag => (
          <li key={tag.tagLabel} role="listitem" {...(!isDesktopViewPort && { tabIndex: 0 })}>
            {tag.tagLabel}
          </li>
        ))}
      </StyledTags>
    );

  const isDesktopViewPort = useCheckBreakpoint(breakPointKey); //is the view port greater than 1200px or 992px
  const { width: viewportWidth } = useWindowSize();
  // making sure the viewport width is defined
  const hasResolvedViewport = typeof viewportWidth === 'number';
  const isPageLoading = !hasResolvedViewport;
  let imageRendition = dynamicMedia;
  if (cardVariation === 'tall' && dynamicMediaTall && isDesktopViewPort) {
    imageRendition = dynamicMediaTall;
  } else if (cardVariation === 'wide' && dynamicMediaWide && isDesktopViewPort) {
    imageRendition = dynamicMediaWide;
  }
  const isBrandToken = !!brandLogoIcon?.startsWith('mcom-common:brands/');
  const brand = isBrandToken ? brandLogoIcon?.split('/').pop() : '';
  const brandLogoIconPath =
    isBrandToken && brand
      ? `https://www.dev-cdn.marriott.com/mi-assets/mi-global/brand-logos/${brand}.svg`
      : brandLogoIcon;
  const isPendingDcaTracking =
    !!trackingProperties?.isDCAEnabled &&
    !trackingProperties?.trackingContentPosition &&
    !trackingProperties?.trackingDescription &&
    !trackingProperties?.trackingOfferType &&
    !trackingProperties?.trackingTag &&
    !trackingProperties?.atCCeVar48;

  useEffect(() => {
    /*
      For mobile and tablet accesibility, Setting the focus to subtitle on the click of expand button
      Since the subitle appears earlier in the DOM, So focus is set manually via ref and tabIndex -1
    */
    if (isExpanded) {
      if (pdcRef.current) {
        pdcRef.current?.focus();
      }
    }
  }, [isExpanded]);

  const isImageLoaded = useImageLoadTracker(pdcRef, [
    hasResolvedViewport,
    fileReferenceImage,
    dynamicMedia,
    dynamicMediaTall,
    dynamicMediaWide,
    isDesktopViewPort,
  ]);
  const isCardLoading = isPageLoading || !isImageLoaded;

  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  const descriptionId = description ? `${id}description` : undefined;
  const shouldDescribeLink = descriptionId && (isDesktopViewPort || isExpanded);

  return (
    <StyledProductDetailsCard
      $isExpanded={isExpanded}
      cardVariation={cardVariation}
      breakPointKey={breakPointKey}
      className={clsx(
        `pdc-${cardVariation} inverse`,
        !isImageLoaded && 'skeleton-loader',
        isCardLoading && 'page-loading',
        className,
        styleclass
      )}
      data-testid="homepage-ProductDetailsCard"
      data-component-name="o-homepage-ProductDetailsCard"
      id={componentId}
    >
      <Button
        key={
          isPendingDcaTracking
            ? 'pending-dca-tracking'
            : `${trackingProperties?.trackingContentPosition || cardUrl}|${
                trackingProperties?.trackingDescription || ''
              }`
        }
        isLink
        href={cardUrl}
        target={openInaNewTab ? '_blank' : '_self'}
        linkType={openInaNewTab ? 'external' : 'internal'}
        className={clsx('pdc-card', { 'is-expanded': !isDesktopViewPort && isExpanded })}
        rel={openInaNewTab ? 'noopener noreferrer' : undefined}
        {...(shouldDescribeLink && {
          ariaDescribedBy: descriptionId,
        })}
        trackingProperties={trackingProperties}
        ref={pdcRef}
      >
        {hasResolvedViewport && (
          <Image
            altText={imageAltText}
            renditions={imageRendition?.renditions}
            defaultImageURL={fileReferenceImage}
            customClass="pdc-image"
          />
        )}
        {!isCardLoading && (
          <div className="pdc-content">
            <div className="pdc-content-top">
              {eyebrow && (
                <StyledEyebrow id={`${id}eyebrow`}>
                  {icon ? <Icon iconClass={clsx(getIconClassFromToken(icon), 'icon-xs')} /> : null}
                  <Eyebrow text={eyebrow} variation={EyebrowEnum.Overlay} />
                </StyledEyebrow>
              )}
              {title && (
                <StyledTitleRow id={`${id}title`}>
                  <Heading
                    variation={headingType.subtitle}
                    titleText={title}
                    fontSize={size.extraLarge}
                    element={tags.paragraph}
                    customClass="m-0 m-ellipsis-2lines"
                  />
                  <Icon iconClass={clsx('icon-arrow-right', 'icon-l')} />
                </StyledTitleRow>
              )}
              {badgeText || tagListItems?.length ? (
                <TagsWrapper>
                  {badgeText && (
                    <StyledBadge
                      badgeText={badgeText}
                      badgeIcon={badgeIcon && getIconClassFromToken(badgeIcon)}
                      badgeVariation={Types.BadgeVariation.Overlay}
                      id={`${id}badge`}
                    />
                  )}
                  {renderTags(tagListItems)}
                </TagsWrapper>
              ) : null}
              {subTitle && (
                <div id={`${id}subtitle`} className="pdc-hovercontent pt-2">
                  <Heading
                    variation={headingType.body}
                    titleText={subTitle}
                    fontSize={size.small}
                    element={tags.paragraph}
                    customClass="m-0"
                  />
                </div>
              )}
              {description && (
                <p id={descriptionId} role="note" className="t-font-s pdc-hovercontent m-0 pt-2">
                  {description}
                </p>
              )}
            </div>
            <div className="pdc-content-bottom">
              {brandLogoIcon && (
                <div className="pdc-brand-logo" id={`${id}logo`}>
                  {isBrandToken ? (
                    <LogoContainer
                      ariaRole="note"
                      alignment="Centered"
                      brand={brand || ''}
                      aspectRatio="3:2"
                      width={78}
                      height={52}
                      logopath={brandLogoIconPath}
                      color="background-aware"
                      backgroundColor={LogoContainerBackgroundColor.Inverse}
                    />
                  ) : (
                    <img
                      src={brandLogoIconPath}
                      alt=""
                      className="pdc-brand-logo-image"
                      data-testid="pdc-brand-logo-image"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Button>
      {!isCardLoading && (
        <button
          type="button"
          className={`pdc-mobile-toggle ${isExpanded ? 'is-expanded' : ''}`}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Close additional information' : 'Expand to see more information'}
          onClick={toggleExpand}
          onMouseDown={e => {
            e.stopPropagation();
          }}
          onTouchStart={e => {
            e.stopPropagation();
          }}
        >
          <span className={`icon-info ${isExpanded ? 'd-none' : 'd-flex'}`}>
            <svg width="3" height="16" viewBox="0 0 3 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="3" height="9" transform="translate(0 6.5)" fill="#1C1C1C" />
              <rect width="3" height="3" transform="translate(0 0.5)" fill="#1C1C1C" />
            </svg>
          </span>
          <span className={`icon-clear ${isExpanded ? 'd-flex' : 'd-none'}`} />
        </button>
      )}
    </StyledProductDetailsCard>
  );
};

export const ProductDetailsCard = (props: ProductDetailsCardProps) => {
  return (
    <EditableComponent
      config={{
        emptyLabel: 'Product Details Card',
        isEmpty: () => false,
        resourceType: 'mi-aem-homepage-spa/components/content/productdetailscard',
      }}
      {...props}
    >
      <ProductDetailsCardComp {...props} />
    </EditableComponent>
  );
};
